// Central field-hint registry for the feature editor (and wizard).
//
// One source of truth for "what is this field for" and "what happens if I
// leave it blank". Keep this in sync when a field's consumers change; the
// editor renders these via hintFor(<key>).
import type { ReactNode } from "react";

export interface FieldHint {
  /** One line: what the field is for. */
  purpose: string;
  /** What visibly happens / degrades when it's left empty. */
  ifEmpty: string;
}

export const FIELD_HINTS: Record<string, FieldHint> = {
  name: {
    purpose: "The launch's title — the card headline and search title.",
    ifEmpty: "Cards and search results show a blank title.",
  },
  pitch: {
    purpose: "One-line summary of what the feature is.",
    ifEmpty:
      "The Timeline card falls back to the announcement narrative; if that's empty too, the card shows no body.",
  },
  feature_family: {
    purpose: "Optional grouping label to cluster related launches.",
    ifEmpty: "Omitted from the card footer and search subtitle. No functional effect.",
  },
  flag_ids: {
    purpose:
      "The squapi flag(s) gating this launch. The first is primary — it drives rollout, workspaces, and the Auto access tier.",
    ifEmpty:
      "No rollout signal — an Auto-state launch resolves to nothing and won't appear on the Timeline.",
  },
  owner_email: {
    purpose: "Point of contact to ask about this launch.",
    ifEmpty: "The card omits the owner; Analytics lists it under 'no owner'.",
  },
  platforms: {
    purpose:
      "Which platforms this launch targets. The note beside each shows on the card and detail.",
    ifEmpty: "No platform shown on the card.",
  },
  links_notes: {
    purpose: "Free-form links / context — PRD, demo video, Linear tickets, etc.",
    ifEmpty: "The card and detail omit the links / notes block.",
  },
  audience: {
    purpose:
      "Who this launch is for — a specific customer/account if there is one (marquee logos matter), otherwise the persona or segment.",
    ifEmpty: "The Audience row is left blank.",
  },
  announcement_state: {
    purpose:
      "Access tier. Auto tracks the flag; pin EA / GA to override what the flag implies. (The pre-EA 'Not yet available' tier is derived automatically — it can't be pinned.)",
    ifEmpty:
      "Auto — derived from the primary flag. If the flag implies no state, the launch won't reach the Timeline.",
  },
  ea_date: {
    purpose: "Early-Access date. A past date reads as actual, a future one as a target.",
    ifEmpty: "No EA date recorded.",
  },
  ga_date: {
    purpose:
      "The single GA target — a concrete GA date, or a coarse forward target (TBD / quarter / week). A concrete date is a real milestone and appears on the Timeline; a coarse target is a projection and doesn't. Forward targets are hidden from the GTM role.",
    ifEmpty: "No GA date or target recorded.",
  },
  announcement_narrative: {
    purpose: "Customer-facing message shown on the Timeline card.",
    ifEmpty: "The card falls back to the pitch.",
  },
  beta: {
    purpose: "Readiness — Beta vs Stable — independent of access (EA/GA).",
    ifEmpty: "Defaults to Stable.",
  },
  known_gaps: {
    purpose: "Known bugs or missing pieces shipping with the launch, one per line.",
    ifEmpty: "No gaps section shown on the card or detail.",
  },
};

/** Render a field's hint as tooltip content: purpose + an if-empty note. */
function FieldHintBody({ hint }: { hint: FieldHint }) {
  return (
    <span className="block space-y-1">
      <span className="block">{hint.purpose}</span>
      <span className="block text-text-tertiary">If empty: {hint.ifEmpty}</span>
    </span>
  );
}

/** Tooltip content for a field key, or null if unknown. Pass to a `hint` prop. */
export function hintFor(key: keyof typeof FIELD_HINTS | string): ReactNode {
  const hint = FIELD_HINTS[key];
  return hint ? <FieldHintBody hint={hint} /> : null;
}
